"use client";

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MegaMenuStickyMode } from "../ui/lib/megaMenuSticky";
const SMART_DELTA_PX = 12;
const STUCK_AFTER_PX = 8;
const SCROLLABLE_OVERFLOW = new Set(["auto", "scroll", "overlay"]);
export function readMegaMenuScrollY(target: EventTarget): number {
  if (target && typeof target === "object" && "scrollTop" in target) {
    const top = (target as HTMLElement).scrollTop;
    if (typeof top === "number") {
      return top;
    }
  }
  if (typeof window === "undefined") {
    return 0;
  }
  if (target === window || target === document) {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }
  return 0;
}

/**
 * Window plus every overflow:auto/scroll ancestor.
 * Do not require current overflow — builder `.pb-canvas-scroll-host` is the
 * sticky containing block even before the canvas is taller than the viewport.
 */
export function collectMegaMenuScrollTargets(node: HTMLElement | null): EventTarget[] {
  if (typeof window === "undefined") {
    return [];
  }
  const targets: EventTarget[] = [window];
  let parent = node?.parentElement ?? null;
  while (parent) {
    const {
      overflowY
    } = window.getComputedStyle(parent);
    if (SCROLLABLE_OVERFLOW.has(overflowY)) {
      targets.push(parent);
    }
    parent = parent.parentElement;
  }
  return targets;
}
export function maxMegaMenuScrollY(targets: EventTarget[]): number {
  let max = 0;
  for (const target of targets) {
    const y = readMegaMenuScrollY(target);
    if (y > max) {
      max = y;
    }
  }
  return max;
}
export type MegaMenuStickyScrollState = {
  lastY: number;
  retracted: boolean;
  stuck: boolean;
};

/** Pure smart-hide step — keeps header visible until scroll is past the top zone. */
export function computeMegaMenuStickyScrollState(y: number, lastY: number, {
  lockVisible,
  retracted,
  smartEnabled,
  stuckAfterPx
}: {
  smartEnabled: boolean;
  lockVisible: boolean;
  stuckAfterPx: number;
  retracted: boolean;
}): MegaMenuStickyScrollState {
  const stuck = y > stuckAfterPx;
  if (!smartEnabled) {
    return {
      stuck,
      retracted: false,
      lastY: y
    };
  }
  if (lockVisible || y <= stuckAfterPx) {
    return {
      stuck,
      retracted: false,
      lastY: y
    };
  }
  const delta = y - lastY;
  if (delta > SMART_DELTA_PX) {
    const nextRetracted = lastY > stuckAfterPx && y > stuckAfterPx ? true : retracted;
    return {
      stuck,
      retracted: nextRetracted,
      lastY: y
    };
  }
  if (delta < -SMART_DELTA_PX) {
    return {
      stuck,
      retracted: false,
      lastY: y
    };
  }
  return {
    stuck,
    retracted,
    lastY
  };
}
export function useMegaMenuStickyHeader(t0) {
  const $ = _c(19);
  const {
    enabled,
    mode,
    lockVisible,
    previewMode,
    rootRef,
    stuckAfterPx: t1
  } = t0;
  const stuckAfterPx = t1 === undefined ? STUCK_AFTER_PX : t1;
  const [stuck, setStuck] = useState(false);
  const [retracted, setRetracted] = useState(false);
  const lastYRef = useRef(0);
  const retractedRef = useRef(false);
  const frameRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const targetsRef = useRef(t2);
  const smartEnabled = enabled && mode === "smart" && !previewMode;
  let t3;
  if ($[1] !== lockVisible || $[2] !== smartEnabled || $[3] !== stuckAfterPx) {
    t3 = () => {
      const y = maxMegaMenuScrollY(targetsRef.current);
      const next = computeMegaMenuStickyScrollState(y, lastYRef.current, {
        lockVisible,
        retracted: retractedRef.current,
        smartEnabled,
        stuckAfterPx
      });
      lastYRef.current = next.lastY;
      retractedRef.current = next.retracted;
      setStuck(next.stuck);
      setRetracted(next.retracted);
    };
    $[1] = lockVisible;
    $[2] = smartEnabled;
    $[3] = stuckAfterPx;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const applyScroll = t3;
  let t4;
  if ($[5] !== applyScroll || $[6] !== enabled || $[7] !== rootRef) {
    t4 = () => {
      if (!enabled || typeof window === "undefined") {
        return _temp;
      }
      const targets = collectMegaMenuScrollTargets(rootRef.current);
      targetsRef.current = targets;
      lastYRef.current = maxMegaMenuScrollY(targets);
      retractedRef.current = false;
      applyScroll();
      const onScroll = () => {
        if (frameRef.current != null) {
          return;
        }
        frameRef.current = window.requestAnimationFrame(() => {
          frameRef.current = null;
          applyScroll();
        });
      };
      const resync = () => {
        lastYRef.current = maxMegaMenuScrollY(targetsRef.current);
        applyScroll();
      };
      for (const target of targets) {
        target.addEventListener("scroll", onScroll, {
          passive: true
        });
      }
      window.addEventListener("pageshow", resync);
      const raf1 = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resync);
      });
      return () => {
        for (const target_0 of targets) {
          target_0.removeEventListener("scroll", onScroll);
        }
        window.removeEventListener("pageshow", resync);
        window.cancelAnimationFrame(raf1);
        if (frameRef.current != null) {
          window.cancelAnimationFrame(frameRef.current);
          frameRef.current = null;
        }
      };
    };
    $[5] = applyScroll;
    $[6] = enabled;
    $[7] = rootRef;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const bindScrollListeners = t4;
  let t5;
  let t6;
  if ($[9] !== bindScrollListeners || $[10] !== enabled) {
    t5 = () => {
      if (!enabled || typeof window === "undefined") {
        setStuck(false);
        setRetracted(false);
        retractedRef.current = false;
        return;
      }
      return bindScrollListeners();
    };
    t6 = [bindScrollListeners, enabled];
    $[9] = bindScrollListeners;
    $[10] = enabled;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useLayoutEffect(t5, t6);
  let t7;
  let t8;
  if ($[13] !== lockVisible) {
    t7 = () => {
      if (lockVisible) {
        retractedRef.current = false;
        setRetracted(false);
      }
    };
    t8 = [lockVisible];
    $[13] = lockVisible;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  const t9 = enabled && stuck;
  const t10 = enabled && retracted;
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = {
      stuck: t9,
      retracted: t10
    };
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
}
function _temp() {}